import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { IPagedDataTable } from '../interfaces/IPagedDataTable.interface';
import { map, publish, publishReplay, switchMap } from 'rxjs/operators';

@Injectable()
export class ODataApiClientService {

    constructor(private http: HttpClient) { }

    /**
     * Sends multiple GET requests to OData API endpoint which returns paged data to retrieve all records.
     * @param url OData API endpoint which returns paged data
     * @param top Number of records which API endpoint should return. Default value: 1000
     * @returns Observable of all records as Array<T>. T represents a type of API endpoint records payload
     * @example getAll<Category>(`api/v1/categories`, 100) returns [{id: 1, name: 'cat1'}, {id: 2, name: 'cat2'}]
     */    
    public getAll<T>(url: string, top: number = 1000): Observable<Array<T>> {
        return this.http.get<IPagedDataTable<T>>(`${url}?$skip=${0}&$top=${top}`).pipe(switchMap((pagedData: IPagedDataTable<T>) => {
            const pagedRecords: Array<T> = pagedData.pagedRecords;
            if (pagedData.totalNumberOfRecords / top > 1) {
                let numberOfNewRequest: number = 0;

                let paginationOffset = top; // because we already have first page
                while(paginationOffset < pagedData.totalNumberOfRecords) {
                    numberOfNewRequest++;
                    paginationOffset += top;
                }              

                if (numberOfNewRequest) {
                    const requests: Array<Observable<IPagedDataTable<T>>> = new Array<Observable<IPagedDataTable<T>>>();
                    for (let currentRequest = 1; currentRequest <= numberOfNewRequest; currentRequest++) {
                        const skip = currentRequest * top;
                        requests.push(this.http.get<IPagedDataTable<T>>(`${url}?$skip=${skip}&$top=${top}`));
                    }
                    return forkJoin(requests)
                        .pipe(map((responses: Array<IPagedDataTable<T>>) => {
                            responses.forEach((response: IPagedDataTable<T>) => {
                                pagedRecords.push(...response.pagedRecords);
                            });
                            return pagedRecords;
                        }));
                }
            }
            return of(pagedRecords);
        }))
    }
}